import React, { useState, useRef } from 'react';
import { Briefcase, GraduationCap, Scale, ChevronRight, Mail, Loader2, CheckCircle2, AlertCircle, Heart, BookOpen, Users, Zap } from 'lucide-react';
import { COUNTRY_CODES, getFlagEmoji } from './Countrycodes';

const CareerSection = () => {
  const brandColors = {
    bgWhite: "#FFFFFF",
    blackPrimary: "#1A1A1A",
    textGray: "#4A4A4A",
    crimson: "#A10F1A",
    gold: "#C79A3B",
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    role: 'Junior Associate',
  });

  const [cvFile, setCvFile]         = useState(null);
  const [cvFileError, setCvFileError]   = useState('');

  const [clFile, setClFile]         = useState(null);
  const [clFileError, setClFileError]   = useState('');

  const [idFile, setIdFile]         = useState(null);
  const [idFileError, setIdFileError]   = useState('');

  const [emailStatus, setEmailStatus] = useState({ loading: false, success: false, error: '' });
  const formRef = useRef(null);

  const API_URL = 'http://localhost:5000/api/apply';

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
    setFormData({ ...formData, phone: digitsOnly });
  };

  const makeFileHandler = (setFile, setError, allowedTypes, hint) => (e) => {
    const file = e.target.files[0];
    if (!file) { setFile(null); return; }
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      setError(hint);
      setFile(null);
      e.target.value = '';
      return;
    }
    if (file.size > maxSize) {
      setError('File is too large — max size is 5MB.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setError('');
    setFile(file);
  };

  const handleCvFileChange = makeFileHandler(
    setCvFile, setCvFileError,
    ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    'Please upload a PDF or Word document.'
  );

  const handleClFileChange = makeFileHandler(
    setClFile, setClFileError,
    ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    'Please upload a PDF or Word document.'
  );

  const handleIdFileChange = makeFileHandler(
    setIdFile, setIdFileError,
    ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'],
    'Please upload a PDF or Image (JPG/PNG).'
  );

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    if (!/^[0-9]{6,15}$/.test(formData.phone)) {
      setEmailStatus({ loading: false, success: false, error: 'Please enter a valid phone number (digits only).' });
      return;
    }
    let hasError = false;
    if (!cvFile)  { setCvFileError('Please upload your CV/Resume.');      hasError = true; }
    if (!clFile)  { setClFileError('Please upload your Cover Letter.');   hasError = true; }
    if (!idFile)  { setIdFileError('Please upload your ID Proof.');       hasError = true; }
    if (hasError) return;

    setEmailStatus({ loading: true, success: false, error: '' });
    try {
      const data = new FormData();
      data.append('name',        formData.name);
      data.append('email',       formData.email);
      data.append('countryCode', formData.countryCode);
      data.append('phone',       formData.phone);
      data.append('role',        formData.role);
      data.append('cv',          cvFile);
      data.append('coverLetter', clFile);
      data.append('idProof',     idFile);

      const res = await fetch(API_URL, { method: 'POST', body: data });
      const responseData = await res.json().catch(() => ({}));
      if (!res.ok || !responseData.success) throw new Error(responseData.message || 'Something went wrong. Please try again.');
      setEmailStatus({ loading: false, success: true, error: '' });
    } catch (err) {
      setEmailStatus({ loading: false, success: false, error: err.message || 'Failed to send. Please check your connection and try again.' });
    }
  };

  const inputStyle = {
    borderColor: "rgba(26,26,26,0.15)",
    backgroundColor: brandColors.bgWhite,
  };

  const focusGold  = (e) => (e.target.style.borderColor = brandColors.gold);
  const blurNormal = (e) => (e.target.style.borderColor = "rgba(26,26,26,0.15)");

  const culturePoints = [
    { icon: Heart,    title: "Family First",       desc: "We treat every team member as family — with open-door communication and genuine respect for personal life." },
    { icon: BookOpen, title: "Grow Together",       desc: "Knowledge sharing, upskilling, and structured training build stimulating, rewarding careers at every stage." },
    { icon: Users,    title: "Vibrant Community",   desc: "Family Day, Annual Day, Wellness Programs and Offsites ensure there is always play alongside work." },
    { icon: Zap,      title: "Hybrid Flexibility",  desc: "We follow a hybrid work model that gives you the freedom to plan your schedule and maintain balance." },
  ];

  return (
    <>
  
      {/* ─────────────────────── CAREER SECTION ─────────────────────── */}
      <section id="career-apply" className="py-16 md:py-24 px-5 md:px-12 bg-white relative overflow-hidden">

        {/* Background Watermark */}
        <div
          className="absolute top-10 left-5 md:left-10 pointer-events-none select-none z-0"
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: "clamp(3rem, 12vw, 120px)",
            fontWeight: 700,
            color: "rgba(26, 26, 26, 0.02)",
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          Career
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* SECTION HEADER */}
          <div className="mb-12 md:mb-16 text-center flex flex-col items-center">
            <div className="flex items-center gap-2 md:gap-3 mb-4">
              <span style={{ width: "30px", height: "1px", background: `linear-gradient(270deg, ${brandColors.crimson}, transparent)` }} />
              <span
                style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: "11px",
                  letterSpacing: "0.2em",
                  color: brandColors.crimson,
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Join Our Team
              </span>
              <span style={{ width: "30px", height: "1px", background: `linear-gradient(90deg, ${brandColors.crimson}, transparent)` }} />
            </div>
            <h2
              style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }}
              className="text-3xl md:text-5xl font-bold uppercase tracking-wide leading-tight"
            >
              Build Your <span style={{ color: brandColors.gold }}>Legacy</span>
            </h2>
            <div
  className="relative mt-6 w-full p-8 md:p-10 bg-white border overflow-hidden"
  style={{ borderColor: "rgba(26,26,26,0.08)", borderRadius: "var(--border-radius-lg)" }}
>
  {/* Left accent line */}
  <div className="absolute top-0 left-0 w-[3px] h-full bg-gradient-to-b from-[#A10F1A] to-[#C79A3B]" />

  {/* Intro text */}
  <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }}
    className="text-base md:text-lg font-medium leading-relaxed">
    There is no other way to say this better… we are looking for a <em>'family member'</em>! @ the Firm,
    thoughts flow freely, ideas are channelized, and with an 'open door policy' any restrictions on
    communications are eliminated. We @ R & P Law Partners Offices India are proud of each member and
    respect their personal lives and help them achieve the desired professional objectives.
  </p>

  {/* Culture points grid */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6 pt-6 border-t"
    style={{ borderColor: "rgba(26,26,26,0.08)" }}>
    {culturePoints.map(({ icon: Icon, title, desc }) => (
      <div key={title} className="flex gap-3 items-start">
        <Icon size={18} style={{ color: brandColors.gold }} className="mt-0.5 flex-shrink-0" />
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }}
            className="text-[10px] font-bold uppercase tracking-widest mb-1">{title}</p>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }}
            className="text-sm font-medium leading-relaxed">{desc}</p>
        </div>
      </div>
    ))}
  </div>

  {/* Email footer */}
  <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }}
    className="text-sm font-medium mt-6 pt-5 border-t" style={{ borderColor: "rgba(26,26,26,0.08)" }}>
    Join us to be a part of one of the most heart-warming institutions. To apply, write to us at{" "}
    <a href="mailto:admin@rplawpartners.com" style={{ color: brandColors.crimson }} className="font-semibold">admin@rplawpartners.com</a>{" "}
    and <a href="mailto:ritika@rplawpartners.com" style={{ color: brandColors.crimson }} className="font-semibold">ritika@rplawpartners.com</a>
  </p>
</div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 lg:items-start">

            {/* LEFT: Vacancies */}
            <div className="flex flex-col gap-6 md:gap-8 lg:max-h-[850px] lg:overflow-y-auto lg:pr-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <h3
                style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }}
                className="text-xl md:text-2xl font-bold uppercase tracking-wide border-b border-gray-200 pb-3 md:pb-4 mb-2 sticky top-0 bg-white z-10"
              >
                Current Openings
              </h3>

              {/* Vacancy 1 */}
              <div className="group relative p-6 md:p-8 bg-white border transition-all duration-500 hover:-translate-y-1 shadow-sm hover:shadow-xl" style={{ borderColor: "rgba(26,26,26,0.08)" }}>
                <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-[#A10F1A] to-[#C79A3B] transform scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" />
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span style={{ fontFamily: "'Cinzel', serif", color: brandColors.crimson }} className="block text-[11px] font-bold tracking-[0.2em] mb-1">01</span>
                    <h4 style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }} className="text-lg md:text-xl font-bold uppercase group-hover:text-[#A10F1A] transition-colors">Junior Associate</h4>
                    <p style={{ color: brandColors.gold }} className="text-xs md:text-sm font-semibold tracking-wider mt-1 uppercase">Full-Time · Delhi/Noida</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full" style={{ color: brandColors.gold }}><Briefcase size={20} /></div>
                </div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }} className="text-sm md:text-base font-medium leading-relaxed mb-4">
                  We are looking for a dedicated Junior Associate with 0–2 years of experience. The role involves comprehensive legal research, drafting pleadings and assisting senior counsels in court appearances.
                </p>
                
              </div>

              {/* Vacancy 2 */}
              <div className="group relative p-6 md:p-8 bg-white border transition-all duration-500 hover:-translate-y-1 shadow-sm hover:shadow-xl" style={{ borderColor: "rgba(26,26,26,0.08)" }}>
                <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-[#A10F1A] to-[#C79A3B] transform scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" />
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span style={{ fontFamily: "'Cinzel', serif", color: brandColors.crimson }} className="block text-[11px] font-bold tracking-[0.2em] mb-1">02</span>
                    <h4 style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }} className="text-lg md:text-xl font-bold uppercase group-hover:text-[#A10F1A] transition-colors">Associate</h4>
                    <p style={{ color: brandColors.gold }} className="text-xs md:text-sm font-semibold tracking-wider mt-1 uppercase">Full-Time · Delhi/Noida</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full" style={{ color: brandColors.gold }}><Scale size={20} /></div>
                </div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }} className="text-sm md:text-base font-medium leading-relaxed mb-4">
                  We are seeking an Associate with 3–5 years of independent litigation experience. The role involves handling matters independently, mentoring junior associates and direct client interaction.
                </p>
                
              </div>

              {/* Vacancy 3 */}
              <div className="group relative p-6 md:p-8 bg-white border transition-all duration-500 hover:-translate-y-1 shadow-sm hover:shadow-xl" style={{ borderColor: "rgba(26,26,26,0.08)" }}>
                <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-[#C79A3B] to-[#A10F1A] transform scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" />
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span style={{ fontFamily: "'Cinzel', serif", color: brandColors.crimson }} className="block text-[11px] font-bold tracking-[0.2em] mb-1">03</span>
                    <h4 style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }} className="text-lg md:text-xl font-bold uppercase group-hover:text-[#C79A3B] transition-colors">Internship</h4>
                    <p style={{ color: brandColors.gold }} className="text-xs md:text-sm font-semibold tracking-wider mt-1 uppercase">Minimum 4 Weeks · On-Site</p>
                  </div>
                  <div className="p-2 md:p-3 bg-gray-50 rounded-full" style={{ color: brandColors.gold }}><GraduationCap size={20} /></div>
                </div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColors.textGray }} className="text-sm md:text-base font-medium leading-relaxed mb-4">
                  A hands-on opportunity for current law students to gain real-world exposure. Interns will assist with case law research, observing court proceedings and organising case files.
                </p>
                <ul className="space-y-2" style={{ color: brandColors.textGray }}>
                  <li className="flex items-center gap-2 text-xs md:text-sm font-medium"><ChevronRight size={14} style={{ color: brandColors.crimson }} className="flex-shrink-0" /> Pursuing an LL.B. from a Recognized University.</li>
                  
                </ul>
              </div>
            </div>

            {/* RIGHT: Application Form */}
            <div className="flex flex-col lg:sticky lg:top-8 lg:self-start">
              <div className="p-6 md:p-10 bg-white shadow-xl md:shadow-2xl border" style={{ borderColor: "rgba(26,26,26,0.08)" }}>
                <h3 style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }} className="text-xl md:text-2xl font-bold uppercase tracking-wide mb-2">
                  Apply Now
                </h3>
                <p className="text-xs md:text-sm mb-6 md:mb-8 font-medium" style={{ color: brandColors.textGray }}>
                  Fill in your details and attach your documents — your application will be sent directly to our hiring team's inbox.
                </p>

                <form ref={formRef} onSubmit={handleEmailSubmit} className="space-y-4 md:space-y-5">

                  {/* Name */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>Full Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange}
                      className="w-full p-3 border rounded-sm outline-none transition-colors text-sm"
                      style={inputStyle} onFocus={focusGold} onBlur={blurNormal} />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>Phone Number *</label>
                    <div className="flex gap-2">
                      <select name="countryCode" value={formData.countryCode} onChange={handleChange}
                        className="w-[40%] sm:w-1/3 p-3 border rounded-sm outline-none transition-colors cursor-pointer text-sm"
                        style={{ ...inputStyle, color: brandColors.blackPrimary }} onFocus={focusGold} onBlur={blurNormal}>
                        {COUNTRY_CODES.map((c) => (
                          <option key={`${c.iso2}-${c.dial}`} value={c.dial}>
                            {getFlagEmoji(c.iso2)} {c.name} ({c.dial})
                          </option>
                        ))}
                      </select>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" name="phone" required maxLength={15}
                        value={formData.phone} onChange={handlePhoneChange} placeholder="98765 43210"
                        className="w-[60%] sm:w-2/3 p-3 border rounded-sm outline-none transition-colors text-sm"
                        style={inputStyle} onFocus={focusGold} onBlur={blurNormal} />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>Email Address *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full p-3 border rounded-sm outline-none transition-colors text-sm"
                      style={inputStyle} onFocus={focusGold} onBlur={blurNormal} />
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>Position Applied For *</label>
                    <select name="role" value={formData.role} onChange={handleChange}
                      className="w-full p-3 border rounded-sm outline-none transition-colors cursor-pointer text-sm"
                      style={{ ...inputStyle, color: brandColors.blackPrimary }} onFocus={focusGold} onBlur={blurNormal}>
                      <option value="Junior Associate">Junior Associate</option>
                      <option value="Associate">Associate</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>

                  {/* ── Upload 1: CV/Resume ── */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>
                      01 · Upload CV / Resume *
                    </label>
                    <input type="file" name="cv" required accept=".pdf,.doc,.docx" onChange={handleCvFileChange}
                      className="w-full p-2.5 border rounded-sm outline-none transition-colors text-xs md:text-sm cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:cursor-pointer file:text-[10px] file:md:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-[#A10F1A] file:text-white hover:file:bg-[#8a0d15]"
                      style={{ ...inputStyle, color: brandColors.textGray }} onFocus={focusGold} onBlur={blurNormal} />
                    <p className="text-[10px] md:text-xs mt-1.5 opacity-70" style={{ color: brandColors.textGray }}>PDF or Word document, max 5MB.</p>
                    {cvFile && !cvFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: '#15803d' }}>Selected: {cvFile.name}</p>}
                    {cvFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: brandColors.crimson }}>{cvFileError}</p>}
                  </div>

                  {/* ── Upload 2: Cover Letter ── */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>
                      02 · Upload Cover Letter *
                    </label>
                    <input type="file" name="coverLetter" required accept=".pdf,.doc,.docx" onChange={handleClFileChange}
                      className="w-full p-2.5 border rounded-sm outline-none transition-colors text-xs md:text-sm cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:cursor-pointer file:text-[10px] file:md:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-[#C79A3B] file:text-white hover:file:bg-[#b08832]"
                      style={{ ...inputStyle, color: brandColors.textGray }} onFocus={focusGold} onBlur={blurNormal} />
                    <p className="text-[10px] md:text-xs mt-1.5 opacity-70" style={{ color: brandColors.textGray }}>PDF or Word document, max 5MB.</p>
                    {clFile && !clFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: '#15803d' }}>Selected: {clFile.name}</p>}
                    {clFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: brandColors.crimson }}>{clFileError}</p>}
                  </div>

                  {/* ── Upload 3: ID Proof ── */}
                  <div>
                    <label className="block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-2" style={{ color: brandColors.blackPrimary }}>
                      03 · Upload ID Proof (Aadhaar / PAN / Passport) *
                    </label>
                    <input type="file" name="idProof" required accept=".pdf,.jpg,.jpeg,.png" onChange={handleIdFileChange}
                      className="w-full p-2.5 border rounded-sm outline-none transition-colors text-xs md:text-sm cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:cursor-pointer file:text-[10px] file:md:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-[#1A1A1A] file:text-white hover:file:bg-[#333]"
                      style={{ ...inputStyle, color: brandColors.textGray }} onFocus={focusGold} onBlur={blurNormal} />
                    <p className="text-[10px] md:text-xs mt-1.5 opacity-70" style={{ color: brandColors.textGray }}>PDF or Image (JPG/PNG), max 5MB.</p>
                    {idFile && !idFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: '#15803d' }}>Selected: {idFile.name}</p>}
                    {idFileError && <p className="text-[10px] md:text-xs mt-1 font-medium" style={{ color: brandColors.crimson }}>{idFileError}</p>}
                  </div>

                  {/* Submit */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={emailStatus.loading}
                      className="w-full flex items-center justify-center gap-2 p-3 md:p-4 transition-all duration-300 transform hover:-translate-y-1 group rounded-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                      style={{ backgroundColor: brandColors.crimson, color: brandColors.bgWhite }}
                    >
                      {emailStatus.loading
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Mail size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                      <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: "0.1em" }} className="text-xs md:text-sm uppercase">
                        {emailStatus.loading ? 'Sending...' : 'Submit Application'}
                      </span>
                    </button>
                  </div>

                  {emailStatus.success && (
                    <div className="flex items-center gap-2 text-xs md:text-sm font-medium mt-2" style={{ color: '#15803d' }}>
                      <CheckCircle2 size={16} className="flex-shrink-0" />
                      Your application has been sent to our hiring team. We'll be in touch soon!
                    </div>
                  )}
                  {emailStatus.error && (
                    <div className="flex items-center gap-2 text-xs md:text-sm font-medium mt-2" style={{ color: brandColors.crimson }}>
                      <AlertCircle size={16} className="flex-shrink-0" />
                      {emailStatus.error}
                    </div>
                  )}

                  <p className="text-[10px] md:text-xs text-center mt-2 opacity-60" style={{ color: brandColors.blackPrimary }}>
                    Your application along with all attachments will be sent directly to admin@rplawpartners.com & ritika@rplawpartners.com
                  </p>

                </form>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default CareerSection;